import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { Sections } from 'components/PopoverContent'
import TooltipLabel from 'components/TooltipLabel'
import { buildTranslate } from 'locales'
import { OrderLineItem, PosOrder, PosOrderPrices } from 'models/Order'
import { useMemo } from 'react'
import { currencyFromCents } from 'utilities/formatters'
import Table from './Table'

const t = buildTranslate('users.view.activity.order_details.receipt')

type Props = {
  posOrder: PosOrder
  purchaseTotalCents: number
}

// Ordered like a printed receipt; a null amount renders no row rather than "$0.00".
const TOTAL_ROWS: Array<{ key: string; field: keyof PosOrderPrices }> = [
  { key: 'discount', field: 'discount_cents' },
  { key: 'service_charge', field: 'service_charge_cents' },
  { key: 'subtotal', field: 'subtotal_cents' },
  { key: 'tax', field: 'tax_cents' },
  { key: 'tip', field: 'tip_cents' },
  { key: 'total', field: 'total_cents' },
]

const RECONCILE_TOLERANCE_CENTS = 1

// Only an unparseable quantity defaults to 1; a genuine 0 or negative
// quantity multiplies through as-is, matching what the quantity cell shows.
const toQuantityNumber = (quantity: string | null): number => {
  const parsed = quantity == null ? NaN : parseFloat(quantity)
  return Number.isFinite(parsed) ? parsed : 1
}

// Toast's item price is per unit, so multiply into a line total to match
// digital orders; modifier price is Toast's own additive charge, left as-is.
const toLineTotalItems = (items: Array<OrderLineItem>): Array<OrderLineItem> =>
  items.map(item => ({
    ...item,
    price_cents:
      item.price_cents == null
        ? undefined
        : Math.round(item.price_cents * toQuantityNumber(item.quantity)),
  }))

const DEDUCTION_FIELDS = new Set<keyof PosOrderPrices>(['discount_cents'])

const centsOrZero = (cents: number | null): number => cents ?? 0

const breakdownReconciles = (prices: PosOrderPrices): boolean => {
  const { subtotal_cents: subtotal, total_cents: total } = prices
  if (subtotal === null || total === null) return true

  const serviceCharge = centsOrZero(prices.service_charge_cents)
  const discount = Math.abs(centsOrZero(prices.discount_cents))
  // Only these two sit ambiguously against the sub-total.
  if (serviceCharge === 0 && discount === 0) return true

  const base =
    subtotal + centsOrZero(prices.tax_cents) + centsOrZero(prices.tip_cents)

  return [0, serviceCharge].some(charge =>
    [0, discount].some(
      deduction =>
        Math.abs(base + charge - deduction - total) <= RECONCILE_TOLERANCE_CENTS
    )
  )
}

const formatTotal = (field: keyof PosOrderPrices, cents: number): string => {
  if (!DEDUCTION_FIELDS.has(field)) return currencyFromCents(cents)
  // A zero deduction is not "-$0.00".
  if (cents === 0) return currencyFromCents(0)
  return `-${currencyFromCents(Math.abs(cents))}`
}

const OrderReceipt = ({ posOrder, purchaseTotalCents }: Props) => {
  const [css] = useStyletron()

  // Purchase and POS order disagree on ~1.2% of matched orders. Display with a note to
  // indicate the discrepancy.
  const posTotalCents = posOrder.prices.total_cents
  const totalsDisagree =
    posTotalCents !== null &&
    posTotalCents !== undefined &&
    Math.abs(posTotalCents - purchaseTotalCents) > RECONCILE_TOLERANCE_CENTS

  // Each note hangs off the row it is about. The discount and service charge are
  // ambiguous against the sub-total, so that is where the merchant asks the question.
  const noteFor = (field: keyof PosOrderPrices): Sections => {
    if (field === 'subtotal_cents' && !breakdownReconciles(posOrder.prices)) {
      return [{ content: t('totals_notes.adjustments') }]
    }
    if (field === 'total_cents' && totalsDisagree) {
      return [{ content: t('totals_notes.amount_charged') }]
    }
    return []
  }

  const totalRowClass = css({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
  })
  const termClass = css({ margin: 0 })

  const channelLabel = posOrder.channel
    ? t(`channels.${posOrder.channel}`) || posOrder.channel
    : null

  const statusLabel = t(`statuses.${posOrder.status}`) || posOrder.status

  const lineTotalItems = useMemo(
    () => toLineTotalItems(posOrder.items ?? []),
    [posOrder.items]
  )

  const metadata = [
    { key: 'order_id', value: posOrder.uid },
    { key: 'status', value: statusLabel },
    ...(channelLabel ? [{ key: 'channel', value: channelLabel }] : []),
  ]

  return (
    <div>
      <Text.Header4 bold tag="div" className="mt-l mb-s">
        {t('heading')}
      </Text.Header4>

      {metadata.map(({ key, value }, index) => (
        <div key={key}>
          {/* Same label rhythm as the purchase blocks above the receipt. */}
          <Text.BodyText4
            tag="div"
            bold
            className={index === 0 ? 'mb-xs' : 'mb-xs mt-m'}
          >
            {t(key)}
          </Text.BodyText4>
          <Text.BodyText4 tag="div" color="grey70">
            {value}
          </Text.BodyText4>
        </div>
      ))}

      <Table items={lineTotalItems} />

      <dl className={css({ marginTop: '16px', marginBottom: 0 })}>
        {TOTAL_ROWS.map(({ key, field }) => {
          const cents = posOrder.prices[field]
          if (cents === null || cents === undefined) return null

          const isTotal = field === 'total_cents'
          const label = t(`totals.${key}`)
          const sections = noteFor(field)
          return (
            <div key={key} className={totalRowClass}>
              <dt className={termClass}>
                <Text.BodyText4 bold={isTotal} color="grey70">
                  {sections.length > 0 ? (
                    <TooltipLabel label={label} sections={sections} />
                  ) : (
                    label
                  )}
                </Text.BodyText4>
              </dt>
              <dd className={termClass}>
                <Text.BodyText4 bold={isTotal} color="grey70">
                  {formatTotal(field, cents)}
                </Text.BodyText4>
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}

export default OrderReceipt
